import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output,
    HostBinding, HostListener, ContentChildren, EventEmitter, ViewEncapsulation } from '@angular/core';
import { style, animate, state, transition, trigger } from '@angular/animations';
import { Subject } from 'rxjs/Subject';
import { debounceTime } from 'rxjs/operator/debounceTime';

import { MenuComponent, PADDING_BASE } from './menu.component';

@Component({
    selector: '[jui-submenu]',
    animations: [
        trigger('fadeAnimation', [
            state('*', style({ opacity: 1 })),
            transition('* => void', [
                animate(150, style({ opacity: 0, display: 'none' }))
            ]),
            transition('void => *', [
                style({ opacity: '0' }),
                animate(150, style({ opacity: 1 }))
            ])
        ]),
        trigger('expandAnimation', [
            transition('expand => void', [
                style({ height: '*', overflow: 'hidden' }),
                animate(150, style({ height: 0 }))
            ]),
            transition('void => expand', [
                style({ height: 0, overflow: 'hidden' }),
                animate(150, style({ height: '*' }))
            ])
        ])
    ],
    template: `
        <div class="jui-menu-submenu-title"
            [style.paddingLeft.px]="(menuComponent.mode === 'inline') ? (level*padding) : null"
            (mouseenter)="onMouseEnterEvent($event)"
            (mouseleave)="onMouseLeaveEvent($event)"
            (click)="clickSubMenuTitle()">
            <ng-content select="[title]"></ng-content>
            <ng-template [ngIf]="isShowMoreIcon">
                <i *ngIf="menuComponent.mode === 'vertical'"
                    [class.active]="isOpen"
                    class="iconfont icon-jui-right2 icon-more"></i>
                <i *ngIf="menuComponent.mode === 'horizontal' || menuComponent.mode === 'inline'"
                    [class.active]="isOpen"
                    class="iconfont icon-jui-down2 icon-more"></i>
            </ng-template>
        </div>
        <ul class="jui-menu jui-menu-submenu-sub"
            *ngIf="isOpen"
            [@fadeAnimation]
            [@expandAnimation]="expandState"
            [class.jui-menu-vertical]="menuComponent.mode!=='inline'"
            [class.jui-menu-inline]="menuComponent.mode==='inline'"
            (mouseenter)="onMouseEnterEvent($event)"
            (mouseleave)="onMouseLeaveEvent($event)"
            (click)="clickSubMenuDropDown()">
            <ng-content></ng-content>
        </ul>
    `,
    encapsulation: ViewEncapsulation.None
})

export class SubMenuComponent implements OnInit, AfterViewInit, OnDestroy {
    hostEl: HTMLElement;
    level = 1;
    padding: number = PADDING_BASE;
    _$mouseSubject = new Subject();

    @Input() disabled = false;
    @Input() isShowMoreIcon = true;

    // two way binding
    @Input() isOpen = false;
    @Output() isOpenChange: EventEmitter<boolean> = new EventEmitter();

    get expandState() {
        if (this.isOpen && this.menuComponent.mode !== 'vertical') {
            return 'expand';
        }
        return null;
    }
    get subItemSelected(): boolean {
        return !!this.menuComponent.menuItems.find((e) => {
            return e.selected && e.subMenuComponent === this;
        });
    }
    get submenuSelected(): boolean {
        return !!this.subMenus._results.find((e) => {
            return e !== this && e.subItemSelected;
        });
    }

    @HostBinding('class.jui-menu-submenu')
    get setMenuSubMenuClass() {
        return true;
    }
    @HostBinding('class.jui-menu-submenu-disabled')
    get setMenuDisableClass() {
        return this.disabled;
    }
    @HostBinding('class.jui-menu-submenu-horizontal')
    get setMenuHorizontalClass() {
        return (this.menuComponent.mode === 'horizontal');
    }
    @HostBinding('class.jui-menu-submenu-vertical')
    get setMenuVerticalClass() {
        return (this.menuComponent.mode === 'vertical');
    }
    @HostBinding('class.jui-menu-submenu-inline')
    get setMenuInlineClass() {
        return (this.menuComponent.mode === 'inline');
    }
    @HostBinding('class.jui-menu-submenu-selected')
    get setMenuSubmenuSelectedClass() {
      return this.submenuSelected || this.subItemSelected;
    }

    @ContentChildren(SubMenuComponent) subMenus;

    constructor(
        public menuComponent: MenuComponent
    ) {
        this.menuComponent.setHasSubMenu(true);
        this.menuComponent.subMenus.push(this);
    }

    ngOnInit() {
        debounceTime.call(this._$mouseSubject, 300)
            .subscribe((data: boolean) => {
                this.isOpen = data;
            });
    }
    ngAfterViewInit() {
        if (this.subMenus.length && (this.menuComponent.mode === 'inline')) {
            this.subMenus.filter(x => x !== this).forEach((menu) => {
                setTimeout(() => {
                    menu.level = this.level + 1;
                }, 0);
            });
        }
    }
    ngOnDestroy() {
        this._$mouseSubject.unsubscribe();
    }

    @HostListener('mouseenter', [ '$event' ])
    onMouseEnterEvent(e) {
        if (this.disabled) {
            return false;
        }
        if ((this.menuComponent.mode === 'horizontal') || (this.menuComponent.mode === 'vertical')) {
            this._$mouseSubject.next(true);
        }
    }

    @HostListener('mouseleave', [ '$event' ])
    onMouseLeaveEvent(e) {
        if (this.disabled) {
            return false;
        }
        if ((this.menuComponent.mode === 'horizontal') || (this.menuComponent.mode === 'vertical')) {
            this._$mouseSubject.next(false);
        }
    }

    clickSubMenuTitle() {
        if (this.disabled) {
            return false;
        }
        if (this.menuComponent.mode === 'inline') {
            this.isOpen = !this.isOpen;
            this.isOpenChange.emit(this.isOpen);
        }
    }
    clickSubMenuDropDown() {
        if (this.disabled) {
            return false;
        }
        if ((this.menuComponent.mode === 'vertical') || (this.menuComponent.mode === 'horizontal')) {
            this._$mouseSubject.next(false);
            this.isOpen = false;
            this.isOpenChange.emit(this.isOpen);
        }
    }
}
